import { useState, useRef, useEffect } from 'react';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';
import { 
  HelpCircle, 
  ArrowUpDown, 
  Star, 
  Clock, 
  Ban as Bandage, 
  ArrowUp, 
  ArrowDown, 
  Shield,
  Trash2,
  ChevronDown,
  Users
} from 'lucide-react';
import { useEmbedStore } from '../../lib/embedStore';
import { useUserStore } from '../../lib/userStore';

interface ActionLegendProps {
  language: 'en' | 'fr';
  isBpmSession: boolean;
  partnerLimitedActions?: boolean;
}

export function ActionLegend({ language, isBpmSession, partnerLimitedActions = false }: ActionLegendProps) {
  const [showLegend, setShowLegend] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();
  const isTslhSession = useUserStore((s) => s.is_tslh_session);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const isTslhEmbedSession = partnerLimitedActions || (isEmbed && !!isTslhSession);

  // Auto-scroll vers le modal quand il s'ouvre
  useEffect(() => {
    if (showLegend && !anchoredModal && modalRef.current) {
      const timer = setTimeout(() => {
        modalRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100); // Petit délai pour laisser le modal s'afficher
      return () => clearTimeout(timer);
    }
  }, [showLegend, anchoredModal]);

  const legendItems = [
    {
      key: 'actions-menu',
      icon: <span className="btn btn-sm btn-outline-primary"><ChevronDown size={12} /></span>,
      title: language === 'fr' ? 'Menu Actions' : 'Actions Menu',
      description: language === 'fr' 
        ? 'Cliquez sur ce bouton pour révéler toutes les actions disponibles pour le joueur.'
        : 'Click this button to reveal all available actions for the player.',
      category: 'interface'
    },
    {
      key: 'details',
      icon: <span className="btn btn-sm btn-outline-info"><HelpCircle size={12} /></span>,
      title: language === 'fr' ? 'Détails' : 'Details',
      description: language === 'fr'
        ? 'Ouvre la fiche complète du joueur.'
        : 'Opens the player detail page.',
      category: 'interface'
    },
    {
      key: 'swap',
      icon: <span className="btn btn-sm btn-outline-secondary"><ArrowUpDown size={12} /></span>,
      title: language === 'fr' ? 'Swap (Échange)' : 'Swap',
      description: language === 'fr' 
        ? 'Sélectionne le joueur pour l\'échanger de position avec un autre joueur de votre équipe.'
        : 'Selects the player to swap positions with another player on your team.',
      category: 'management'
    },
    {
      key: 'swap-4-players',
      icon: <span className="btn btn-sm btn-outline-warning"><Users size={12} /></span>,
      title: language === 'fr' ? 'Swap 4 joueurs' : '4-player swap',
      description: language === 'fr'
        ? 'En vue d\'ensemble, activez "Swap 4 Joueurs", puis sélectionnez 2 joueurs du club principal et 2 joueurs des réserves. Ensuite, cliquez sur "Compléter la Permutation (4 Joueurs)".'
        : 'In Overview, enable "4 Players Swap", then select 2 main club players and 2 reserve players. Then click "Complete 4-Player Swap".',
      category: 'swaps'
    },
    {
      key: 'trading-bloc',
      icon: <span className="btn btn-sm btn-outline-warning"><Star size={12} /></span>,
      title: language === 'fr' ? 'Trading Bloc' : 'Trading Bloc',
      description: language === 'fr' 
        ? 'Ajoute ou retire le joueur du bloc d\'échange. Les joueurs dans le bloc sont visibles aux autres équipes.'
        : 'Adds or removes player from trading bloc. Players in bloc are visible to other teams for trades.',
      category: 'trading'
    },
    {
      key: 'intouchable',
      icon: <span className="btn btn-sm btn-outline-info"><Shield size={12} /></span>,
      title: language === 'fr' ? 'Protégé (Intouchable)' : 'Protected (Untouchable)',
      description: language === 'fr' 
        ? 'Marque le joueur comme intouchable pour le protéger des demandes d\'échange automatiques.'
        : 'Marks player as untouchable to protect from automatic trade requests.',
      category: 'trading'
    },
    {
      key: 'promote',
      icon: <span className="btn btn-sm btn-outline-success"><ArrowUp size={12} /></span>,
      title: language === 'fr' ? 'Promouvoir' : 'Promote',
      description: language === 'fr' 
        ? 'Fait passer un joueur des réserves au club principal. Respecte les limites de l\'effectif et de la masse salariale.'
        : 'Moves a player from reserves to main club. Respects roster limits.',
      category: 'actions'
    },
    {
      key: 'demote',
      icon: <span className="btn btn-sm btn-outline-info"><ArrowDown size={12} /></span>,
      title: language === 'fr' ? 'Rétrograder' : 'Demote',
      description: language === 'fr' 
        ? 'Fait passer un joueur du club principal vers les réserves. Limité par les règles de la ligue.'
        : 'Moves a player from main club to reserves. Limited by league rules.',
      category: 'actions'
    },
    {
      key: 'ltir',
      icon: <span className="btn btn-sm btn-outline-danger"><Bandage size={12} /></span>,
      title: language === 'fr' ? 'LTIR' : 'Injured List',
      description: language === 'fr' 
        ? 'Place le joueur sur LTIR (IR) ou affiche les informations de blessure.'
        : 'Places player on injured reserve (IR) or shows injury information.',
      category: 'status'
    },
    {
      key: 'scratch',
      icon: <span className="btn btn-sm btn-outline-warning">📋</span>,
      title: language === 'fr' ? 'Scratch' : 'Scratch',
      description: language === 'fr' 
        ? 'Déplace le joueur vers la liste des joueurs inactifs (scratch). Le salaire compte mais pas les points.'
        : 'Moves player to inactive/scratch list. Salary counts but no points.',
      category: 'management'
    },
    {
      key: 'activate-scratch',
      icon: <span className="btn btn-sm btn-outline-success">✅</span>,
      title: language === 'fr' ? 'Activer (Retour du Scratch)' : 'Activate (Return from Scratch)',
      description: language === 'fr' 
        ? 'Ramène le joueur du scratch vers le club principal. Doit respecter les limites d\'effectif.'
        : 'Returns player from scratch to main club. Must respect roster limits.',
      category: 'management'
    },
    {
      key: 'reactivate-ltir',
      icon: <span className="btn btn-sm btn-outline-warning"><Clock size={12} /></span>,
      title: language === 'fr' ? 'Réactiver (LTIR)' : 'Reactivate (Injured)',
      description: language === 'fr' 
        ? 'Réactive un joueur lors de son retour de LTIR. Affiche le nombre de jours restants.'
        : 'Reactivates injured player on return. Shows remaining days.',
      category: 'status'
    },
    {
      key: 'release',
      icon: <span className="btn btn-sm btn-outline-danger"><Trash2 size={12} /></span>,
      title: language === 'fr' ? 'Libérer' : 'Release',
      description: language === 'fr' 
        ? 'Libère le joueur et le retire de votre équipe définitivement. Le joueur devient agent libre.'
        : 'Releases the player and removes them from your team permanently. Player becomes free agent.',
      category: 'actions'
    }
  ];

  const bpmAllowedActions = new Set(['release', 'swap', 'ltir', 'reactivate-ltir']);
  const tslhAllowedActions = new Set(['details', 'release', 'ltir', 'reactivate-ltir']);
  const visibleLegendItems = isBpmSession
    ? legendItems.filter((item) => bpmAllowedActions.has(item.key))
    : isTslhEmbedSession
      ? legendItems.filter((item) => tslhAllowedActions.has(item.key))
      : legendItems;

  const categories = {
    interface: { 
      title: language === 'fr' ? 'Interface' : 'Interface',
      color: 'secondary'
    },
    actions: { 
      title: language === 'fr' ? 'Actions principales' : 'Main Actions',
      color: 'primary'
    },
    management: { 
      title: language === 'fr' ? 'Gestion d\'équipe' : 'Team Management',
      color: 'info'
    },
    trading: { 
      title: language === 'fr' ? 'Échanges' : 'Trading',
      color: 'success'
    },
    status: { 
      title: language === 'fr' ? 'Statuts spéciaux' : 'Special Status',
      color: 'warning'
    },
    swaps: {
      title: language === 'fr' ? 'Permutation (4 joueurs)' : 'Swaps (4 players)',
      color: 'warning'
    }
  };

  return (
    <>
      {/* Bouton d'aide inline */}
      <button
        className="btn btn-outline-info btn-sm d-flex align-items-center gap-sm-2"
        onClick={() => setShowLegend(true)}
        title={language === 'fr' ? 'Aide - Légende des actions' : 'Help - Action Legend'}
      >
        <HelpCircle size={16} />
        <span className="d-none d-sm-inline">
          {language === 'fr' ? 'Légende des actions' : 'Action Legend'}
        </span>
      </button>

      {/* Modal de légende */}
      {showLegend && (
        <MyTeamsModal onClose={() => setShowLegend(false)}>
          {/* Overlay */}
          <div 
            className="position-fixed top-0 start-0 w-100 h-100"
            data-team-modal-overlay
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.5)', 
              zIndex: 1050 
            }}
            onClick={() => setShowLegend(false)}
          />
          
          {/* Modal Content */}
          <div 
            ref={modalRef}
            data-team-modal-card
            className="position-fixed top-50 start-50 translate-middle bg-white rounded shadow-lg p-4 action-legend-modal"
            style={{ 
              zIndex: 1051, 
              width: '90vw',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: '1px solid #dee2e6'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0 fw-bold text-primary">
                <HelpCircle size={24} className="me-2" />
                {language === 'fr' ? 'Légende des Actions' : 'Action Legend'}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowLegend(false)}
                aria-label="Close"
              ></button>
            </div>

            <div className="mb-3">
              <p className="text-muted small">
                {language === 'fr' 
                  ? 'Voici la liste de toutes les actions disponibles pour gérer vos joueurs :'
                  : 'Here is the list of all available actions to manage your players:'}
              </p>
              <div className="alert alert-info action-legend-mobile-help py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
                <strong>{language === 'fr' ? 'Sur mobile : Appuyez sur le bouton "Actions" sur chaque carte de joueur pour révéler toutes ces options.' : 'On mobile:'}</strong>                
              </div>
            </div>

            <div className="row g-3">
              {Object.entries(categories).map(([categoryKey, categoryInfo]) => {
                const categoryItems = visibleLegendItems.filter(item => item.category === categoryKey);
                if (categoryItems.length === 0) return null;
                
                return (
                  <div key={categoryKey} className="col-12">
                    <h6 className={`fw-bold mb-3 text-${categoryInfo.color}`}>
                      {categoryInfo.title}
                    </h6>
                    <div className="row g-2">
                      {categoryItems.map((item, index) => (
                        <div key={`${categoryKey}-${index}`} className="col-12">
                          <div className="card border-0 bg-light">
                            <div className="card-body p-3">
                              <div className="d-flex align-items-start">
                                <div className="me-3 d-flex align-items-center justify-content-center" style={{ minWidth: '60px' }}>
                                  {item.icon}
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="fw-bold mb-1">{item.title}</h6>
                                  <p className="mb-0 small text-muted">{item.description}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-3 border-top">
              <div className="d-flex align-items-center text-muted small">
                <Clock size={16} className="me-2" />
                {language === 'fr' 
                  ? 'Note: Certaines actions ont un délai de refroidissement de quelques secondes après utilisation.'
                  : 'Note: Some actions have a cooldown period of a few seconds after use.'}
              </div>
            </div>
          </div>
        </MyTeamsModal>
      )}
    </>
  );
}
